import type { CheckStatus } from "@central-hub/contracts";
import { useEffect, useRef, useState } from "react";
import { Sparkline } from "./Charts";
import { ago, clock, duration, timeZoneName } from "./format";
import { VIEWS, type View } from "./hooks";
import { Icon } from "./icons";
import { detailHref } from "./links";
import type {
  BackupRow,
  CertRow,
  ClusterView,
  Hero as HeroModel,
  OpenIncidentRow,
  ResolvedRow,
  TargetCard,
  ViewModel,
} from "./model";
import { erroringSites, formatUptime, STATUS_LABEL } from "./model";
import { Overview } from "./Overview";

/* ---------------- header ---------------- */

export interface HeaderProps {
  vm: ViewModel | null;
  now: number;
  stale: boolean;
  updatedText: string;
  theme: "dark" | "light";
  compact: boolean;
  kiosk: boolean;
  /** The view shown, highlighted in the menu. */
  view: View;
  /** `infra`: may open the Cluster and Security views. */
  user: { name: string; role: string; infra?: boolean } | null;
  onToggleTheme(): void;
  onToggleDensity(): void;
  onToggleKiosk(): void;
  onSignOut(): void;
  onTestNotification?: () => Promise<string>;
  /** Opens the quick search (also Ctrl/⌘ K). */
  onSearch?: () => void;
}

export function Header(props: HeaderProps) {
  const { vm, now, stale, kiosk } = props;
  const hubBad = vm ? vm.hub.failing.length : 0;
  return (
    <header className="topbar">
      <div className="brand">
        <Icon name="logo" />
        <span>Central Hub</span>
      </div>

      <nav aria-label="Views">
        <ul className="views">
          {VIEWS.filter(
            (v) =>
              (!v.admin || props.user?.role === "admin") &&
              (!v.infra || props.user?.infra !== false),
          ).map((v) => (
            <li key={v.id}>
              <a href={v.href} aria-current={v.id === props.view ? "page" : undefined}>
                {v.label}
                {v.id === "deploys" && vm?.deploys && vm.deploys.running.length > 0 && (
                  <span className="views-badge num" title="Deploying now">
                    {vm.deploys.running.length}
                  </span>
                )}
                {v.id === "errors" && vm && erroringSites(vm) > 0 && (
                  <span
                    className="views-badge views-badge-bad num"
                    title="Sites with fatal errors, or whose application is reporting errors"
                  >
                    {erroringSites(vm)}
                  </span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {vm && (
        <ul className="counts" aria-label="Checks by status" data-stale={stale}>
          {vm.counts.map(({ status, n }) => (
            <li key={status} className="count" data-status={status} data-zero={n === 0}>
              <Icon name={status} />
              <b className="num">{n}</b>
              <span>{STATUS_LABEL[status].toLowerCase()}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="spacer" />

      <div className="topbar-right">
        <p className="clock num">
          {clock(now)} <small>{timeZoneName()}</small>
        </p>
        <p className="updated num" role="status" data-stale={stale}>
          <span className={stale ? "dot alarm" : "dot pulse"} aria-hidden="true" />
          <span>{props.updatedText}</span>
        </p>
        {vm && (
          <p
            className="hubhealth"
            data-bad={hubBad > 0 && !stale}
            title={`${vm.hub.total - hubBad}/${vm.hub.total} internal checks up`}
          >
            <Icon name={stale ? "unknown" : hubBad ? "down" : "up"} />
            <span>{stale ? "Hub ?" : hubBad ? `Hub: ${hubBad} failing` : "Hub ok"}</span>
          </p>
        )}
        <div className="tools">
          {props.onSearch && !kiosk && (
            <button
              type="button"
              className="searchbtn"
              onClick={props.onSearch}
              aria-label="Quick search"
              aria-keyshortcuts="Control+K Meta+K"
            >
              <Icon name="search" />
              <span>Search</span>
              <kbd>⌘K</kbd>
            </button>
          )}
          <button
            type="button"
            className="iconbtn"
            onClick={props.onToggleDensity}
            aria-pressed={props.compact}
            aria-label="Compact density"
            title="Compact density"
          >
            <Icon name="density" />
          </button>
          <button
            type="button"
            className="iconbtn"
            onClick={props.onToggleTheme}
            aria-label={props.theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            title={props.theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          >
            <Icon name={props.theme === "dark" ? "sun" : "moon"} />
          </button>
          <button
            type="button"
            className="iconbtn"
            onClick={props.onToggleKiosk}
            aria-pressed={kiosk}
            aria-label={kiosk ? "Exit kiosk mode" : "Kiosk mode (fullscreen)"}
            title={kiosk ? "Exit kiosk mode" : "Kiosk mode (fullscreen)"}
          >
            <Icon name={kiosk ? "shrink" : "expand"} />
          </button>
          {!kiosk && props.user && (
            <UserMenu
              user={props.user}
              onSignOut={props.onSignOut}
              {...(props.onTestNotification && { onTestNotification: props.onTestNotification })}
            />
          )}
        </div>
      </div>
    </header>
  );
}

function UserMenu({
  user,
  onSignOut,
  onTestNotification,
}: {
  user: { name: string; role: string };
  onSignOut(): void;
  onTestNotification?: () => Promise<string>;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const initials =
    user.name
      .replace(/@.*/, "")
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?";

  return (
    <div className="usermenu" ref={ref}>
      <button
        type="button"
        className="iconbtn userbtn"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account menu"
      >
        <span className="avatar" aria-hidden="true">
          {initials}
        </span>
        <Icon name="chevron" />
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-head">
            <p>Signed in as ({user.role})</p>
            <p>{user.name}</p>
          </div>
          {onTestNotification && (
            <button
              type="button"
              role="menuitem"
              className="menuitem"
              onClick={async () => {
                setNote("Sending…");
                setNote(await onTestNotification());
              }}
            >
              <Icon name="bell" />
              Send a test notification
            </button>
          )}
          {note && <p className="menu-note">{note}</p>}
          <button type="button" role="menuitem" className="menuitem" onClick={onSignOut}>
            <Icon name="signout" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- hero ---------------- */

const HERO_STATUS: Record<HeroModel["tone"], CheckStatus> = {
  ok: "up",
  degraded: "degraded",
  down: "down",
  unknown: "unknown",
  empty: "unknown",
};

export function Hero({
  hero,
  now,
  stale,
}: {
  hero: HeroModel;
  now: number;
  stale: { since: number | null; reason: string } | null;
}) {
  if (stale) {
    return (
      <section className="hero" data-tone="stale" aria-live="polite" aria-label="Overall state">
        <div className="hero-main">
          <Icon name="down" className="alarm" label="Connection lost" />
          <div>
            <h1>Connection lost — data below is stale</h1>
            <p className="hero-detail">
              Can’t refresh: {stale.reason}.
              {stale.since ? ` Last successful update at ${clock(stale.since)}.` : ""} Statuses
              shown may no longer be true.
            </p>
          </div>
        </div>
        {stale.since && (
          <div className="hero-side num">
            <p>Last update</p>
            <p>{duration(now - stale.since)} ago</p>
          </div>
        )}
      </section>
    );
  }
  const status = HERO_STATUS[hero.tone];
  return (
    <section
      className="hero"
      data-tone={hero.tone}
      data-status={status}
      aria-live="polite"
      aria-label="Overall state"
    >
      <div className="hero-main">
        <Icon
          name={status}
          className={hero.tone === "down" ? "alarm" : undefined}
          label={hero.title}
        />
        <div>
          <h1>{hero.title}</h1>
          <p className="hero-detail">{hero.detail}</p>
        </div>
      </div>
      {hero.longestSince !== undefined && (
        <div className="hero-side num">
          <p>Longest outage</p>
          <p>{duration(now - hero.longestSince)}</p>
        </div>
      )}
    </section>
  );
}

/* ---------------- board ---------------- */

export function Board({ vm, now, stale }: { vm: ViewModel; now: number; stale: boolean }) {
  return (
    <main className="board" data-stale={stale}>
      <div className="board-main">
        {vm.hub.failing.length > 0 && !stale && <HubAlert failing={vm.hub.failing} />}
        {vm.open.length > 0 && <OpenIncidents rows={vm.open} now={now} />}
        <Overview vm={vm} />
        <section aria-labelledby="targets-title">
          <div className="targets-head">
            <h2 id="targets-title" className="eyebrow">
              Targets
            </h2>
            <span className="num">{vm.targetsMeta}</span>
          </div>
          <ul className="grid">
            {vm.cards.map((card) => (
              <li key={card.name}>
                <Card card={card} compact={vm.compact} />
              </li>
            ))}
          </ul>
        </section>
      </div>
      <aside className="rail" aria-label="Cluster, certificates, backups and history">
        <Cluster cluster={vm.cluster} />
        <Deploys deploys={vm.deploys} now={now} />
        <Certificates certs={vm.certs} />
        <Backups backups={vm.backups} />
        <Resolved rows={vm.resolved} now={now} />
      </aside>
    </main>
  );
}

function HubAlert({ failing }: { failing: ViewModel["hub"]["failing"] }) {
  return (
    <section className="hubalert" aria-labelledby="hub-title">
      <div className="hubalert-title">
        <Icon name="down" className="alarm" />
        <h2 id="hub-title">Monitor impaired — alerts may not reach you</h2>
      </div>
      <ul>
        {failing.map((f) => (
          <li key={f.check} data-status={f.status}>
            <span className="mono">{f.check}</span>
            <span>{f.message}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function OpenIncidents({ rows, now }: { rows: OpenIncidentRow[]; now: number }) {
  return (
    <section
      className="incidents"
      data-down={rows.some((r) => r.status === "down")}
      aria-labelledby="open-title"
    >
      <div className="incidents-head">
        <h2 id="open-title">Open incidents</h2>
        <span className="num muted">{rows.length}</span>
        <span className="eyebrow">Started · ongoing for</span>
      </div>
      <ul>
        {rows.map((row) => (
          <li key={row.id} className="incident" data-status={row.status}>
            <span className="badge" data-status={row.status}>
              <Icon name={row.status} />
              {STATUS_LABEL[row.status].toUpperCase()}
            </span>
            <p className="incident-what">
              <b>{row.target}</b>
              <span className="mono">{row.check}</span>
            </p>
            <p className="incident-msg ellipsis" title={row.message}>
              {row.message}
            </p>
            <p className="incident-when num">
              <span>{clock(row.openedAt)}</span>
              <span>{duration(now - row.openedAt)}</span>
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Card({ card, compact }: { card: TargetCard; compact: boolean }) {
  const label = STATUS_LABEL[card.status];
  if (compact) {
    return (
      <article
        className="card"
        data-status={card.status}
        data-problem={card.problem}
        aria-label={`${card.name}: ${label}`}
      >
        <div className="tile">
          <Icon name={card.status} label={label} />
          <div className="tile-body">
            <div className="tile-title">
              <h3 className="ellipsis">{card.name}</h3>
              {card.problem && <span className="flag">{label}</span>}
              {card.deploy?.running && <span className="flag flag-deploy">Deploying</span>}
              {card.quiet?.kind === "maintenance" && (
                <span className="flag flag-quiet" title={`Alerts held: ${card.quiet.reason}`}>
                  Maintenance
                </span>
              )}
            </div>
            <p className="tile-sub mono ellipsis" title={card.summary}>
              {card.summary}
            </p>
          </div>
          <div className="tile-side num">
            <p>{card.upRatio}</p>
            {card.uptime30d !== null && <p className="muted">{formatUptime(card.uptime30d)}</p>}
          </div>
        </div>
        <a className="card-link" href={detailHref(card.name)}>
          <span className="sr-only">Details for {card.name}</span>
        </a>
      </article>
    );
  }
  return (
    <article
      className="card"
      data-status={card.status}
      data-problem={card.problem}
      aria-label={`${card.name}: ${label}`}
    >
      <header className="card-head">
        <Icon name={card.status} label={label} />
        <h3 className="ellipsis">{card.name}</h3>
        {card.problem && <span className="flag">{label}</span>}
        {card.deploy?.running && <span className="flag flag-deploy">Deploying</span>}
        {card.quiet?.kind === "maintenance" && (
          <span className="flag flag-quiet" title={`Alerts held: ${card.quiet.reason}`}>
            Maintenance
          </span>
        )}
        {card.uptime30d !== null && (
          <span className="card-uptime num" title="Worst 30-day availability of its checks">
            30 d {formatUptime(card.uptime30d)}
          </span>
        )}
      </header>
      <ul className="rows">
        {card.checks.map((row) => (
          <li key={row.name} className="row" data-status={row.status} data-bad={row.bad}>
            <Icon name={row.status} label={STATUS_LABEL[row.status]} />
            <div className="ellipsis">
              <p className="row-name mono ellipsis">{row.name}</p>
              {row.message && (
                <p className="row-msg ellipsis" title={row.message}>
                  {row.message}
                </p>
              )}
            </div>
            <Sparkline values={row.spark} bad={row.bad} />
            <span className="row-lat num">{row.latency}</span>
          </li>
        ))}
      </ul>
      {card.certProblem && (
        <p className="certnote" data-status={card.certProblem.status}>
          <Icon name="lock" />
          <span className="mono">
            {card.certProblem.check} · {card.certProblem.text}
          </span>
        </p>
      )}
      <a className="card-link" href={detailHref(card.name)}>
        <span className="sr-only">Details for {card.name}</span>
      </a>
    </article>
  );
}

/* ---------------- cluster ---------------- */

/** Nodes with their load and the sites they carry, then storage, certificates and stuck pods. */
export function Cluster({ cluster }: { cluster: ClusterView | null }) {
  if (!cluster) return null;
  const parts = [
    ["Storage", cluster.storage],
    ["Certificates", cluster.certificates],
    ["Pods", cluster.pods],
  ] as const;
  return (
    <section className="panel cluster" aria-labelledby="cluster-title">
      <div className="panel-head">
        <h2 id="cluster-title" className="eyebrow">
          <a href="#/cluster">Cluster</a>
        </h2>
        <span className={cluster.status === "up" ? "num" : "num summary-warn"}>
          {cluster.nodes.length} {cluster.nodes.length === 1 ? "node" : "nodes"}
        </span>
      </div>
      <ul className="cluster-nodes">
        {cluster.nodes.map((n) => (
          <li
            key={n.alias}
            className="cluster-node"
            data-status={n.status}
            data-bad={n.status !== "up"}
          >
            <div className="cluster-node-head" title={n.node}>
              <Icon name={n.status} label={STATUS_LABEL[n.status]} />
              <span className="mono ellipsis">{n.alias}</span>
              <span className="num">{n.pods} pods</span>
            </div>
            <Meter label="CPU" value={n.cpu} spark={n.cpuSpark} />
            <Meter label="Memory" value={n.memory} spark={n.memorySpark} />
            {n.status !== "up" && <p className="cluster-message">{n.message}</p>}
            {n.targets.length > 0 && (
              <p className="cluster-targets" title={n.targets.join(", ")}>
                {n.targets.join(" · ")}
              </p>
            )}
          </li>
        ))}
      </ul>
      <ul className="cluster-parts">
        {parts.map(([label, part]) =>
          part ? (
            <li
              key={label}
              className="cluster-part"
              data-status={part.status}
              data-bad={part.status !== "up"}
            >
              <Icon name={part.status} label={STATUS_LABEL[part.status]} />
              <span className="cluster-part-text">
                <span className="cluster-part-label">{label}</span> {part.text}
              </span>
            </li>
          ) : null,
        )}
      </ul>
    </section>
  );
}

/** A usage bar with its figure; amber from 75 %, the node check itself turns degraded at 90 %. */
function Meter({
  label,
  value,
  spark,
}: {
  label: string;
  value: number | null;
  spark: readonly (number | null)[];
}) {
  const pct = value === null ? null : Math.round(value * 100);
  const level = pct === null ? "none" : pct >= 90 ? "high" : pct >= 75 ? "warn" : "ok";
  return (
    <div className="meter" data-level={level}>
      <span className="meter-label">{label}</span>
      {/* The figure next to it says the same; the bar only helps the eye. */}
      <span className="meter-bar" aria-hidden="true">
        <span className="meter-fill" style={{ width: `${Math.min(100, pct ?? 0)}%` }} />
      </span>
      <span className="meter-value num">{pct === null ? "—" : `${pct}%`}</span>
      <Sparkline values={spark} bad={level === "high"} max={1} />
    </div>
  );
}

export function Certificates({ certs }: { certs: ViewModel["certs"] }) {
  if (certs.shown.length === 0) return null;
  return (
    <section className="panel" aria-labelledby="certs-title">
      <div className="panel-head">
        <h2 id="certs-title" className="eyebrow">
          Certificates
        </h2>
        <span className={certs.needAttention ? "num summary-warn" : "num"}>{certs.summary}</span>
      </div>
      <ul className="certs">
        {certs.shown.map((c) => (
          <CertItem key={`${c.target}/${c.check}`} cert={c} />
        ))}
      </ul>
      {certs.more && <p className="certs-more num">{certs.more}</p>}
    </section>
  );
}

function CertItem({ cert }: { cert: CertRow }) {
  const days = cert.days === null ? "?" : cert.days < 0 ? "expired" : `${cert.days} d`;
  return (
    <li
      className="cert"
      data-status={cert.status}
      data-bad={cert.bad}
      title={`${cert.target} ${cert.check} — ${cert.message}`}
    >
      <div className="cert-line">
        <Icon name={cert.status} label={STATUS_LABEL[cert.status]} />
        <span className="ellipsis">{cert.target}</span>
        <span className="cert-days num">{days}</span>
      </div>
      {cert.bad && (
        <p className="cert-detail mono">
          {cert.check} ·{" "}
          {cert.expiresOn
            ? `${cert.days !== null && cert.days < 0 ? "expired" : "expires"} ${cert.expiresOn}`
            : cert.message}
        </p>
      )}
    </li>
  );
}

/** Deploys going on now, then the latest ones across targets, each linking to its run. */
function Deploys({ deploys, now }: { deploys: ViewModel["deploys"]; now: number }) {
  if (!deploys) return null;
  const { running, recent } = deploys;
  return (
    <section className="panel" aria-labelledby="deploys-title">
      <div className="panel-head">
        <h2 id="deploys-title" className="eyebrow">
          Deploys
        </h2>
        <span className="num">
          {running.length > 0 ? `${running.length} in progress` : "none in progress"}
        </span>
      </div>
      {running.length > 0 && (
        <ul className="deploys" aria-live="polite">
          {running.map((r) => (
            <li key={r.target} className="deploy deploy-running">
              <span className="deploy-pulse" aria-hidden="true" />
              <a className="ellipsis" href={detailHref(r.target)}>
                {r.target}
              </a>
              <span className="num">{duration(now - r.since)}</span>
              {r.url ? (
                <a href={r.url} target="_blank" rel="noopener noreferrer">
                  View ↗
                </a>
              ) : (
                <span />
              )}
            </li>
          ))}
        </ul>
      )}
      <ul className="deploys">
        {recent.length === 0 && <li className="muted">No deploy recorded yet.</li>}
        {recent.map((d) => (
          <li
            key={d.key}
            className="deploy"
            data-status={d.status}
            title={[d.conclusion, d.duration && `took ${d.duration}`, d.actor]
              .filter(Boolean)
              .join(" · ")}
          >
            <Icon name={d.status} label={d.conclusion} />
            <a className="ellipsis" href={detailHref(d.target)}>
              {d.target}
            </a>
            <span className="mono deploy-commit">{d.commit}</span>
            <span className="num deploy-when">{d.when}</span>
            {d.url ? (
              <a
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Run of ${d.target}`}
              >
                ↗
              </a>
            ) : (
              <span />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Backups({ backups }: { backups: ViewModel["backups"] }) {
  if (backups.rows.length === 0) return null;
  return (
    <section className="panel" aria-labelledby="backups-title">
      <div className="panel-head">
        <h2 id="backups-title" className="eyebrow">
          Backups
        </h2>
        <span className={backups.needAttention ? "num summary-warn" : "num"}>
          {backups.summary}
        </span>
      </div>
      <ul className="backups">
        {backups.rows.map((row) => (
          <BackupItem key={row.target} row={row} />
        ))}
      </ul>
    </section>
  );
}

function BackupItem({ row }: { row: BackupRow }) {
  const { backup, restore } = row;
  const failing = (cell: BackupRow["backup"]) =>
    cell !== null && (cell.status === "down" || cell.status === "degraded");
  const problems = [
    ...(failing(backup) ? [`backup: ${backup?.message}`] : []),
    ...(failing(restore) ? [`restore test: ${restore?.message}`] : []),
  ];
  const title = [
    backup && `Backup: ${backup.message}`,
    restore && `Restore test: ${restore.message}`,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <li className="backup" data-status={row.status} data-bad={row.bad} title={title}>
      <div className="backup-line">
        <Icon name={row.status} label={STATUS_LABEL[row.status]} />
        <span className="ellipsis">{row.target}</span>
        <span className="backup-when num">{backup?.text ?? "—"}</span>
        {restore && (
          <span
            className="backup-restore"
            data-status={restore.status}
            title={`Restore test: ${restore.message}`}
          >
            <Icon name={restore.status} label={`restore test ${restore.status}`} />
          </span>
        )}
      </div>
      {problems.map((p) => (
        <p key={p} className="cert-detail mono backup-detail">
          {p}
        </p>
      ))}
    </li>
  );
}

function Resolved({ rows, now }: { rows: ResolvedRow[]; now: number }) {
  return (
    <section className="panel resolved" aria-labelledby="resolved-title">
      <div className="panel-head">
        <h2 id="resolved-title" className="eyebrow">
          Resolved · last 24 h
        </h2>
        <span className="num">{rows.length || ""}</span>
      </div>
      {rows.length === 0 ? (
        <p className="muted">No incidents in the last 24 hours.</p>
      ) : (
        <ul>
          {rows.map((r) => (
            <li key={r.id}>
              <Icon name={r.status} label={`was ${STATUS_LABEL[r.status].toLowerCase()}`} />
              <div className="ellipsis">
                <p>
                  <b>{r.target}</b> <span className="mono">{r.check}</span>
                </p>
                <p className="num">{r.text}</p>
              </div>
              <p
                className="resolved-when num"
                title={`resolved ${ago(now - r.resolvedAt)} — ${r.message}`}
              >
                {clock(r.resolvedAt)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function EmptyBoard() {
  return (
    <main className="center">
      <div className="empty">
        <h2>Nothing to watch yet</h2>
        <p>
          Add a target and its checks to the hub’s configuration. It appears here after the first
          collection run, usually within a minute.
        </p>
        <p className="mono">GET /api/v1/status → {'{ "checks": [] }'}</p>
      </div>
    </main>
  );
}
